import { useIntl } from 'react-intl';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, radiusVars, spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Button } from '@astryxdesign/core/Button';
import { HStack } from '@astryxdesign/core/HStack';
import { VStack } from '@astryxdesign/core/VStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Plus } from 'lucide-react';
import { IconButton } from '@astryxdesign/core/IconButton';
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl';
import { Text } from '@astryxdesign/core/Text';
import type { FilterCondition, FilterNode } from '@qronstat/shared';
import { useApp } from '../../app/context';
import { defaultOperator, filterableDimensions, isCondition } from './model';
import { ConditionRow } from './ConditionRow';
interface Group {
  connector: 'and' | 'or';
  conditions: FilterCondition[];
}
function toGroups(nodes: FilterNode[]): Group[] {
  return nodes.map(n => {
    if (isCondition(n)) return {
      connector: 'and',
      conditions: [n]
    };
    const children = ('and' in n ? n.and : n.or).filter(isCondition);
    return {
      connector: 'and' in n ? 'and' : 'or',
      conditions: children
    };
  });
}
function fromGroups(groups: Group[]): FilterNode[] {
  return groups.filter(g => g.conditions.length).map(g => {
    if (g.conditions.length === 1) return g.conditions[0] as FilterCondition;
    return g.connector === 'or' ? {
      or: g.conditions
    } : {
      and: g.conditions
    };
  });
}
export function ConditionBuilder({
  value,
  onChange,
  narrow,
  scope
}: {
  value: FilterNode[];
  onChange: (value: FilterNode[]) => void;
  narrow?: boolean;
  scope?: readonly string[];
}) {
  const intl = useIntl();
  const {
    meta
  } = useApp();
  const groups = toGroups(value);
  const firstDim = filterableDimensions(meta, scope)[0];
  const blank = (): FilterCondition => ({
    member: firstDim?.id ?? '',
    operator: defaultOperator(firstDim)
  });
  const update = (next: Group[]) => onChange(fromGroups(next));
  return <VStack gap={3}>
      {groups.map((g, gi) => <VStack key={gi} gap={2}>
          {gi > 0 ? <Text size="sm" color="secondary">
              {intl.formatMessage({
          id: 'filters.and'
        })}
            </Text> : null}
          <div className="xmkeg23 x1y0btm7 x14i3s5s x1hviunn x1b2ylru">
            <VStack gap={2}>
              {g.conditions.length > 1 ? <HStack gap={2} vAlign="center" hAlign="between">
                  <SegmentedControl label={intl.formatMessage({
              id: 'filters.connector'
            })} size="sm" value={g.connector} onChange={v => update(groups.map((x, i) => i === gi ? {
              ...x,
              connector: v as 'and' | 'or'
            } : x))}>
                    <SegmentedControlItem value="and" label={intl.formatMessage({
                id: 'filters.allOf'
              })} />
                    <SegmentedControlItem value="or" label={intl.formatMessage({
                id: 'filters.anyOf'
              })} />
                  </SegmentedControl>
                  <IconButton label={intl.formatMessage({
              id: 'filters.removeGroup'
            })} icon={<Icon icon="close" />} variant="ghost" size="sm" onClick={() => update(groups.filter((_, i) => i !== gi))} />
                </HStack> : null}
              {g.conditions.map((c, ci) => <ConditionRow {...scope ? {
            scope
          } : {}} key={ci} condition={c} {...narrow ? {
            narrow: true
          } : {}} onChange={next => update(groups.map((x, i) => i === gi ? {
            ...x,
            conditions: x.conditions.map((y, j) => j === ci ? next : y)
          } : x))} onRemove={() => update(groups.map((x, i) => i === gi ? {
            ...x,
            conditions: x.conditions.filter((_, j) => j !== ci)
          } : x))} />)}
              <HStack>
                <Button label={intl.formatMessage({
              id: g.connector === 'or' ? 'filters.addOr' : 'filters.addAnd'
            })} variant="ghost" size="sm" icon={<Icon icon={Plus} />} onClick={() => update(groups.map((x, i) => i === gi ? {
              ...x,
              conditions: [...x.conditions, blank()]
            } : x))} />
              </HStack>
            </VStack>
          </div>
        </VStack>)}
      <HStack>
        <Button label={intl.formatMessage({
        id: 'filters.addCondition'
      })} size="sm" icon={<Icon icon={Plus} />} onClick={() => update([...groups, {
        connector: 'and',
        conditions: [blank()]
      }])} isDisabled={!firstDim} />
      </HStack>
    </VStack>;
}